import { CategoryTeaser, SearchPageHit, Teaser } from 'app/graphql/__generated__/types/contento';
import { createImageProxyUrl } from 'app/utils/svtimage';
import Link from 'next/link';
import React from 'react';
import { fontWeightBold } from '../../../ui-components/design-tokens';
import PlayBadge, { PlayBadgeSize } from '../../../ui-components/PlayBadge/PlayBadge';
import { SIZE } from '../../../ui-components/typography/PlayText/PlayText';
import useTrackImpression, { OnItemImpressionType } from '../../../utils/useTrackImpression';
import {
    SearchSuggestionEpisodeText,
    SearchSuggestionLink,
    SearchSuggestionLinkByline,
    SearchSuggestionLinkText,
    SearchSuggestionTextWrapper,
    SearchSuggestionThumbnail,
    ThumbNailImage,
} from './AutoCompleteResult.styles';
import { getUrl, stripEms, value } from './helpers';

const getThumbnail = (teaser: Teaser | CategoryTeaser): string => {
    if (teaser.__typename === 'Teaser') {
        return createImageProxyUrl(
            teaser.images.wide.id,
            teaser.images.wide.changed,
            'wide',
            140,
            'quality=90'
        );
    }

    return createImageProxyUrl(teaser.image.id, teaser.image.changed, 'wide', 140, 'quality=90');
};

const getThumbnailAlt = (teaser: Teaser | CategoryTeaser): string | undefined => {
    if (teaser.__typename === 'Teaser') {
        return teaser?.images?.wide?.description;
    }

    return teaser.heading ? `${stripEms(teaser.heading)} bild` : undefined;
};

const getHeading = (teaser: Teaser | CategoryTeaser): string | undefined =>
    teaser.heading ? stripEms(teaser.heading) : undefined;

const getSubheading = (teaser: Teaser | CategoryTeaser): string | undefined => {
    if (teaser.__typename === 'Teaser') {
        stripEms(teaser.subHeading);
    }
    return undefined;
};

type Props = {
    item: SearchPageHit;
    suggestionClick: (value: string) => void;
    itemPosition?: number;
    onItemImpression: OnItemImpressionType;
};

const PopOverLink = ({ item, suggestionClick, itemPosition = 0, onItemImpression }: Props) => {
    const teaser: Teaser | CategoryTeaser = item.teaser || item.categoryTeaser;

    let contentId;

    if (teaser.__typename === 'CategoryTeaser') {
        contentId = teaser.id;
    } else {
        contentId = teaser.item?.svtId;
    }

    const [trackingRef] = useTrackImpression(onItemImpression, itemPosition + 1, contentId);
    const thumbnail = getThumbnail(teaser);
    const thumbnailAltText = getThumbnailAlt(teaser);

    return (
        <Link href={getUrl(item)} passHref>
            <SearchSuggestionLink
                data-rt-spec="auto-complete-link"
                onMouseDown={() => {
                    suggestionClick(value(item));
                }}
                ref={trackingRef}
            >
                {thumbnail && (
                    <SearchSuggestionThumbnail>
                        {teaser.__typename === 'Teaser' && teaser.badge && (
                            <PlayBadge
                                text={teaser.badge.text}
                                theme={teaser.badge.type}
                                accessibilityText={teaser.badge.altText}
                                overlay
                                size={PlayBadgeSize.SMALL}
                            />
                        )}
                        <ThumbNailImage key={thumbnail} src={thumbnail} alt={thumbnailAltText} />
                    </SearchSuggestionThumbnail>
                )}
                <SearchSuggestionTextWrapper>
                    <SearchSuggestionLinkByline as="span">{teaser?.byline}</SearchSuggestionLinkByline>
                    <SearchSuggestionLinkText className="linkText" weight={fontWeightBold} size={SIZE.LARGE}>
                        {getHeading(teaser)}
                    </SearchSuggestionLinkText>
                    <SearchSuggestionEpisodeText>{getSubheading(teaser)}</SearchSuggestionEpisodeText>
                </SearchSuggestionTextWrapper>
            </SearchSuggestionLink>
        </Link>
    );
};

export default PopOverLink;
